/**
 * Vendors — V0.3 marquee module.
 *
 * Society-empanelled vendor management. Three tabs:
 *   1. Vendors      — roster (create, edit, deactivate)
 *   2. Assignments  — complaint → vendor with SLA tracking + resolve
 *   3. Invoices     — vendor payouts (create, mark paid)
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Wrench,
  Plus,
  Edit3,
  Power,
  Filter,
  Star,
  AlertTriangle,
  CheckCircle2,
  Receipt,
  Clock3,
  Phone,
  IndianRupee,
  MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate, formatDateTime } from '@/lib/format';

// ───── types ─────

type VendorCategory =
  | 'plumbing'
  | 'electrical'
  | 'carpentry'
  | 'ac_repair'
  | 'painting'
  | 'pest_control'
  | 'housekeeping'
  | 'gardening'
  | 'lift_maintenance'
  | 'security_system'
  | 'other';

type VendorStatus = 'active' | 'inactive' | 'blacklisted';
type SlaPriority = 'low' | 'normal' | 'high' | 'urgent';
type InvoiceStatus = 'unpaid' | 'paid' | 'cancelled';

interface Vendor {
  id: string;
  name: string;
  category: VendorCategory;
  contact_name: string | null;
  phone: string;
  email: string | null;
  address: string | null;
  notes: string | null;
  status: VendorStatus;
  hourly_rate_inr: number | null;
  rating_avg: number | null;
  jobs_completed: number;
  total_paid_inr: number;
  created_at: string;
  updated_at: string;
  _count?: { assignments: number; invoices: number };
}

interface VendorAssignment {
  id: string;
  vendor_id: string;
  complaint_id: string;
  sla_priority: SlaPriority;
  sla_due_at: string;
  assigned_at: string;
  resolved_at: string | null;
  resolution_notes: string | null;
  resident_rating: number | null;
  vendor?: { id: string; name: string; category: VendorCategory; phone: string };
  complaint?: { id: string; title: string; category: string; priority: string; status: string; body: string };
}

interface VendorInvoice {
  id: string;
  vendor_id: string;
  assignment_id: string | null;
  invoice_number: string;
  amount_inr: number;
  description: string | null;
  invoice_date: string;
  paid_at: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  status: InvoiceStatus;
  created_at: string;
  vendor?: { id: string; name: string; category: VendorCategory };
}

// ───── labels ─────

const CATEGORY_LABEL: Record<VendorCategory, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  carpentry: 'Carpentry',
  ac_repair: 'AC repair',
  painting: 'Painting',
  pest_control: 'Pest control',
  housekeeping: 'Housekeeping',
  gardening: 'Gardening',
  lift_maintenance: 'Lift maintenance',
  security_system: 'Security system',
  other: 'Other'
};

const CATEGORY_OPTIONS: VendorCategory[] = [
  'plumbing',
  'electrical',
  'carpentry',
  'ac_repair',
  'painting',
  'pest_control',
  'housekeeping',
  'gardening',
  'lift_maintenance',
  'security_system',
  'other'
];

const STATUS_LABEL: Record<VendorStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  blacklisted: 'Blacklisted'
};

const STATUS_VARIANT: Record<VendorStatus, 'ok' | 'muted' | 'alert'> = {
  active: 'ok',
  inactive: 'muted',
  blacklisted: 'alert'
};

const SLA_LABEL: Record<SlaPriority, string> = {
  low: 'Low · 7d',
  normal: 'Normal · 48h',
  high: 'High · 24h',
  urgent: 'Urgent · 4h'
};

const SLA_VARIANT: Record<SlaPriority, 'muted' | 'info' | 'warn' | 'alert'> = {
  low: 'muted',
  normal: 'info',
  high: 'warn',
  urgent: 'alert'
};

const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  unpaid: 'Unpaid',
  paid: 'Paid',
  cancelled: 'Cancelled'
};

const INVOICE_STATUS_VARIANT: Record<InvoiceStatus, 'warn' | 'ok' | 'muted'> = {
  unpaid: 'warn',
  paid: 'ok',
  cancelled: 'muted'
};

// ───── form schemas ─────

const VendorFormSchema = z.object({
  name: z.string().trim().min(2, 'Name required').max(160),
  category: z.enum([
    'plumbing',
    'electrical',
    'carpentry',
    'ac_repair',
    'painting',
    'pest_control',
    'housekeeping',
    'gardening',
    'lift_maintenance',
    'security_system',
    'other'
  ]),
  contact_name: z.string().trim().max(120).optional().or(z.literal('')),
  phone: z.string().trim().min(7, 'Phone required').max(20),
  email: z.string().trim().email().max(160).optional().or(z.literal('')),
  address: z.string().trim().max(300).optional().or(z.literal('')),
  notes: z.string().trim().max(500).optional().or(z.literal('')),
  hourly_rate_inr: z.coerce.number().int().min(0).max(100000).optional()
});
type VendorFormValues = z.infer<typeof VendorFormSchema>;

const DEFAULT_VENDOR_FORM: VendorFormValues = {
  name: '',
  category: 'plumbing',
  contact_name: '',
  phone: '',
  email: '',
  address: '',
  notes: '',
  hourly_rate_inr: 0
};

const InvoiceFormSchema = z.object({
  invoice_number: z.string().trim().min(1).max(60),
  amount_inr: z.coerce.number().int().min(0).max(10_000_000),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  invoice_date: z.string().min(1),
  assignment_id: z.string().uuid().optional().or(z.literal('')),
  payment_method: z.string().trim().max(40).optional().or(z.literal(''))
});
type InvoiceFormValues = z.infer<typeof InvoiceFormSchema>;

const ResolveFormSchema = z.object({
  resolution_notes: z.string().trim().min(1, 'Notes required').max(500),
  resident_rating: z.coerce.number().int().min(1).max(5).optional(),
  resident_feedback: z.string().trim().max(500).optional().or(z.literal(''))
});
type ResolveFormValues = z.infer<typeof ResolveFormSchema>;

// ───── helpers ─────

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function ratingLabel(r: number | null): string {
  if (r === null || r === undefined) return '—';
  return r.toFixed(1);
}

function slaCountdownLabel(dueAt: string, resolvedAt: string | null): { label: string; variant: 'ok' | 'warn' | 'alert' | 'muted' } {
  if (resolvedAt) return { label: 'Resolved', variant: 'muted' };
  const dueMs = new Date(dueAt).getTime();
  const diff = dueMs - Date.now();
  if (diff <= 0) {
    const hoursOver = Math.ceil(-diff / (60 * 60 * 1000));
    return { label: `Breached by ${hoursOver}h`, variant: 'alert' };
  }
  const hoursLeft = Math.ceil(diff / (60 * 60 * 1000));
  if (hoursLeft <= 6) return { label: `Due in ${hoursLeft}h`, variant: 'warn' };
  if (hoursLeft <= 48) return { label: `Due in ${hoursLeft}h`, variant: 'info' as never as 'ok' };
  const daysLeft = Math.ceil(hoursLeft / 24);
  return { label: `Due in ${daysLeft}d`, variant: 'ok' };
}

function todayIsoDate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ───── component ─────

type Tab = 'vendors' | 'assignments' | 'invoices';

export function VendorsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('vendors');
  const [deactivateTarget, setDeactivateTarget] = useState<Vendor | null>(null);

  // Filters (vendors tab)
  const [statusFilter, setStatusFilter] = useState<VendorStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<VendorCategory | 'all'>('all');
  const [searchInput, setSearchInput] = useState<string>('');

  // Vendor list with cursor pagination.
  const vendorFiltersHash = useMemo(
    () => JSON.stringify([statusFilter, categoryFilter, searchInput.trim()]),
    [statusFilter, categoryFilter, searchInput]
  );
  const [vendorCursor, setVendorCursor] = useState<string | null>(null);
  useEffect(() => {
    setVendorCursor(null);
  }, [vendorFiltersHash]);
  const vendorsQuery = useQuery({
    queryKey: ['vendors', statusFilter, categoryFilter, searchInput, vendorCursor],
    queryFn: () =>
      get<{ items: Vendor[]; next_cursor: string | null }>('/vendors', {
        params: {
          limit: 50,
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(categoryFilter !== 'all' ? { category: categoryFilter } : {}),
          ...(searchInput.trim().length > 0 ? { q: searchInput.trim() } : {}),
          ...(vendorCursor ? { cursor: vendorCursor } : {})
        }
      })
  });
  const vendorPaginator = useCursorAccumulator<Vendor>(vendorsQuery.data, vendorCursor, vendorFiltersHash);
  const vendors = vendorPaginator.items;

  // Assignments list
  const assignmentsQuery = useQuery({
    queryKey: ['vendor-assignments'],
    queryFn: () =>
      get<{ items: VendorAssignment[]; next_cursor: string | null }>('/vendor-assignments', {
        params: { limit: 100 }
      }),
    enabled: tab === 'assignments' || tab === 'vendors'
  });
  const assignments = assignmentsQuery.data?.items ?? [];

  // Invoices list
  const invoicesQuery = useQuery({
    queryKey: ['vendor-invoices'],
    queryFn: () =>
      get<{ items: VendorInvoice[]; next_cursor: string | null }>('/vendor-invoices', {
        params: { limit: 100 }
      }),
    enabled: tab === 'invoices' || tab === 'vendors'
  });
  const invoices = invoicesQuery.data?.items ?? [];

  // ───── KPIs ─────

  const kpiActive = vendors.filter((v) => v.status === 'active').length;
  const kpiOpenAssignments = assignments.filter((a) => a.resolved_at === null).length;
  const kpiBreached = assignments.filter(
    (a) => a.resolved_at === null && new Date(a.sla_due_at).getTime() < Date.now()
  ).length;
  const kpiUnpaid = invoices
    .filter((i) => i.status === 'unpaid')
    .reduce((sum, i) => sum + i.amount_inr, 0);

  // ───── vendor form ─────

  const [editing, setEditing] = useState<Vendor | null>(null);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const vendorForm = useForm<VendorFormValues>({
    resolver: zodResolver(VendorFormSchema),
    defaultValues: DEFAULT_VENDOR_FORM,
    mode: 'onChange'
  });

  const openCreate = (): void => {
    setEditing(null);
    vendorForm.reset(DEFAULT_VENDOR_FORM);
    setVendorDialogOpen(true);
  };
  const openEdit = (v: Vendor): void => {
    setEditing(v);
    vendorForm.reset({
      name: v.name,
      category: v.category,
      contact_name: v.contact_name ?? '',
      phone: v.phone,
      email: v.email ?? '',
      address: v.address ?? '',
      notes: v.notes ?? '',
      hourly_rate_inr: v.hourly_rate_inr ?? 0
    });
    setVendorDialogOpen(true);
  };

  const saveVendor = useMutation({
    mutationFn: (v: VendorFormValues) => {
      const body = {
        name: v.name,
        category: v.category,
        contact_name: v.contact_name?.trim() || undefined,
        phone: v.phone,
        email: v.email?.trim() || undefined,
        address: v.address?.trim() || undefined,
        notes: v.notes?.trim() || undefined,
        hourly_rate_inr: v.hourly_rate_inr ?? undefined
      };
      if (editing) return patch<Vendor>(`/vendors/${editing.id}`, body);
      return post<Vendor>('/vendors', body);
    },
    onSuccess: () => {
      toast.success(editing ? 'Vendor updated' : 'Vendor created');
      setVendorDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deactivateVendor = useMutation({
    mutationFn: (id: string) => del<Vendor>(`/vendors/${id}`),
    onSuccess: () => {
      toast.success('Vendor deactivated');
      void queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── invoice form ─────

  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceForVendor, setInvoiceForVendor] = useState<Vendor | null>(null);
  const invoiceForm = useForm<InvoiceFormValues>({
    resolver: zodResolver(InvoiceFormSchema),
    mode: 'onChange',
    defaultValues: {
      invoice_number: '',
      amount_inr: 0,
      description: '',
      invoice_date: todayIsoDate(),
      assignment_id: '',
      payment_method: ''
    }
  });

  const openInvoiceDialog = (v: Vendor): void => {
    setInvoiceForVendor(v);
    invoiceForm.reset({
      invoice_number: '',
      amount_inr: 0,
      description: '',
      invoice_date: todayIsoDate(),
      assignment_id: '',
      payment_method: ''
    });
    setInvoiceDialogOpen(true);
  };

  const createInvoice = useMutation({
    mutationFn: (v: InvoiceFormValues) => {
      if (!invoiceForVendor) throw new Error('No vendor selected');
      const body = {
        invoice_number: v.invoice_number,
        amount_inr: v.amount_inr,
        description: v.description?.trim() || undefined,
        invoice_date: new Date(`${v.invoice_date}T00:00:00.000Z`).toISOString(),
        assignment_id: v.assignment_id && v.assignment_id.length > 0 ? v.assignment_id : undefined,
        payment_method: v.payment_method?.trim() || undefined
      };
      return post<VendorInvoice>(`/vendors/${invoiceForVendor.id}/invoices`, body);
    },
    onSuccess: () => {
      toast.success('Invoice created');
      setInvoiceDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['vendor-invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const markInvoicePaid = useMutation({
    mutationFn: (id: string) => post<VendorInvoice>(`/vendor-invoices/${id}/mark-paid`, {}),
    onSuccess: () => {
      toast.success('Invoice marked paid');
      void queryClient.invalidateQueries({ queryKey: ['vendor-invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── resolve assignment ─────

  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolvingAssignment, setResolvingAssignment] = useState<VendorAssignment | null>(null);
  const resolveForm = useForm<ResolveFormValues>({
    resolver: zodResolver(ResolveFormSchema),
    mode: 'onChange',
    defaultValues: { resolution_notes: '', resident_rating: undefined, resident_feedback: '' }
  });

  const openResolveDialog = (a: VendorAssignment): void => {
    setResolvingAssignment(a);
    resolveForm.reset({
      resolution_notes: '',
      resident_rating: undefined,
      resident_feedback: ''
    });
    setResolveDialogOpen(true);
  };

  const resolveAssignment = useMutation({
    mutationFn: (v: ResolveFormValues) => {
      if (!resolvingAssignment) throw new Error('No assignment');
      return post<VendorAssignment>(`/vendor-assignments/${resolvingAssignment.id}/resolve`, {
        resolution_notes: v.resolution_notes,
        resident_rating: v.resident_rating,
        resident_feedback: v.resident_feedback?.trim() || undefined
      });
    },
    onSuccess: () => {
      toast.success('Assignment resolved');
      setResolveDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['vendor-assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── kebab menu state ─────

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const toggleMenu = (id: string): void => setOpenMenuId((cur) => (cur === id ? null : id));

  const totalsAssignmentsByVendor = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of assignments) {
      if (a.resolved_at === null) {
        map.set(a.vendor_id, (map.get(a.vendor_id) ?? 0) + 1);
      }
    }
    return map;
  }, [assignments]);

  // ───── vendor form dialog ─────
  const vendorFormDialog = (
    <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit vendor' : 'Add vendor'}</DialogTitle>
          <DialogDescription>
            Maintain society-empanelled service vendors and their contact info.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={vendorForm.handleSubmit((v) => saveVendor.mutate(v))} className="flex flex-col gap-vt-3">
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="v-name">Vendor name</Label>
              <Input id="v-name" placeholder="Sharma Plumbing Co" {...vendorForm.register('name')} />
              {vendorForm.formState.errors.name ? (
                <p className="text-vt-12 text-vt-alert">{vendorForm.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="v-category">Category</Label>
              <Select id="v-category" {...vendorForm.register('category')}>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="v-contact">Contact person</Label>
              <Input id="v-contact" placeholder="Rakesh Sharma" {...vendorForm.register('contact_name')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="v-phone">Phone</Label>
              <Input id="v-phone" placeholder="+91…" {...vendorForm.register('phone')} />
              {vendorForm.formState.errors.phone ? (
                <p className="text-vt-12 text-vt-alert">{vendorForm.formState.errors.phone.message}</p>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="v-email">Email</Label>
              <Input id="v-email" type="email" placeholder="vendor@example.com" {...vendorForm.register('email')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="v-rate">Hourly rate (₹)</Label>
              <Input id="v-rate" type="number" min={0} {...vendorForm.register('hourly_rate_inr')} />
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="v-address">Address</Label>
            <Input id="v-address" {...vendorForm.register('address')} />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="v-notes">Notes</Label>
            <Textarea id="v-notes" rows={2} {...vendorForm.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setVendorDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saveVendor.isPending}
              loadingText="Saving…"
              disabled={saveVendor.isPending || !vendorForm.formState.isValid}
            >
              {editing ? 'Save changes' : 'Create vendor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  // ───── invoice form dialog ─────
  const invoiceFormDialog = (
    <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create invoice {invoiceForVendor ? `for ${invoiceForVendor.name}` : ''}</DialogTitle>
          <DialogDescription>Record an invoice for vendor work or monthly retainer.</DialogDescription>
        </DialogHeader>
        <form onSubmit={invoiceForm.handleSubmit((v) => createInvoice.mutate(v))} className="flex flex-col gap-vt-3">
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="i-number">Invoice number</Label>
              <Input id="i-number" placeholder="INV-2026-001" {...invoiceForm.register('invoice_number')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="i-amount">Amount (₹)</Label>
              <Input id="i-amount" type="number" min={0} {...invoiceForm.register('amount_inr')} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="i-date">Invoice date</Label>
              <Input id="i-date" type="date" {...invoiceForm.register('invoice_date')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="i-method">Payment method</Label>
              <Select id="i-method" {...invoiceForm.register('payment_method')}>
                <option value="">—</option>
                <option value="bank_transfer">Bank transfer</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
              </Select>
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="i-desc">Description</Label>
            <Textarea id="i-desc" rows={2} placeholder="Plumbing work for B-202" {...invoiceForm.register('description')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setInvoiceDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createInvoice.isPending}
              loadingText="Saving…"
              disabled={createInvoice.isPending || !invoiceForm.formState.isValid}
            >
              Create invoice
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  // ───── resolve assignment dialog ─────
  const resolveDialog = (
    <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resolve assignment</DialogTitle>
          <DialogDescription>
            Record the work done and (optionally) the resident's satisfaction rating.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={resolveForm.handleSubmit((v) => resolveAssignment.mutate(v))} className="flex flex-col gap-vt-3">
          {resolvingAssignment?.complaint ? (
            <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3 text-vt-13 text-vt-text">
              <div className="font-medium">{resolvingAssignment.complaint.title}</div>
              <div className="mt-1 line-clamp-2 text-vt-text-muted">{resolvingAssignment.complaint.body}</div>
            </div>
          ) : null}
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="r-notes">Resolution notes</Label>
            <Textarea id="r-notes" rows={3} placeholder="Replaced kitchen sink valve; tested leak-free." {...resolveForm.register('resolution_notes')} />
            {resolveForm.formState.errors.resolution_notes ? (
              <p className="text-vt-12 text-vt-alert">{resolveForm.formState.errors.resolution_notes.message}</p>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="r-rating">Resident rating (1-5)</Label>
              <Select id="r-rating" {...resolveForm.register('resident_rating')}>
                <option value="">—</option>
                <option value="1">1 - Poor</option>
                <option value="2">2 - Fair</option>
                <option value="3">3 - Average</option>
                <option value="4">4 - Good</option>
                <option value="5">5 - Excellent</option>
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="r-feedback">Resident feedback</Label>
              <Input id="r-feedback" placeholder="Optional comment" {...resolveForm.register('resident_feedback')} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setResolveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={resolveAssignment.isPending}
              loadingText="Saving…"
              disabled={resolveAssignment.isPending || !resolveForm.formState.isValid}
            >
              Mark resolved
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  // ───── render ─────

  return (
    <>
      <PageHeader
        title="Vendors"
        description="Society-empanelled service vendors — assignments, SLAs, payouts."
        actions={
          tab === 'vendors' ? (
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" /> Add vendor
            </Button>
          ) : undefined
        }
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Wrench className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Active vendors</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {vendorsQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiActive}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-info-100)] text-vt-info">
                <Clock3 className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Open assignments</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {assignmentsQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiOpenAssignments}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-alert-100)] text-vt-alert">
                <AlertTriangle className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">SLA breaches now</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {assignmentsQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiBreached}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-vt-warn">
                <IndianRupee className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Unpaid invoices total</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {invoicesQuery.isLoading ? <Skeleton className="h-7 w-20" /> : formatInr(kpiUnpaid)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={[
                { value: 'vendors', label: 'Vendors' },
                { value: 'assignments', label: 'Assignments' },
                { value: 'invoices', label: 'Invoices' }
              ]}
              aria-label="Vendors sections"
            />
          </CardContent>
        </Card>

        {tab === 'vendors' ? (
          <>
            {/* Filters */}
            <Card>
              <CardContent className="flex flex-wrap items-center gap-vt-2 p-vt-4">
                <Filter className="h-4 w-4 text-vt-text-muted" />
                <span className="text-vt-12 text-vt-text-muted">Status:</span>
                <TabsPills
                  size="sm"
                  value={statusFilter}
                  onChange={(v) => setStatusFilter(v as 'all' | VendorStatus)}
                  options={(['all', 'active', 'inactive', 'blacklisted'] as Array<'all' | VendorStatus>).map((s) => ({
                    value: s,
                    label: s === 'all' ? 'All' : STATUS_LABEL[s as VendorStatus]
                  }))}
                  aria-label="Vendor status filter"
                />
                <div className="ml-vt-3 inline-flex items-center gap-vt-2">
                  <Label htmlFor="cat-filter" className="text-vt-12 text-vt-text-muted">
                    Category:
                  </Label>
                  <Select
                    id="cat-filter"
                    className="h-8 w-44 text-vt-12"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value as VendorCategory | 'all')}
                  >
                    <option value="all">All</option>
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {CATEGORY_LABEL[c]}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="ml-vt-3 flex-1 min-w-[200px]">
                  <Input
                    placeholder="Search by name, contact, phone…"
                    className="h-8 text-vt-12"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            <Card>
              <CardContent className="p-vt-4">
                {vendorsQuery.isLoading ? (
                  <div className="flex flex-col gap-vt-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : vendorsQuery.isError ? (
                  <EmptyState
                    icon={<Wrench className="h-6 w-6" />}
                    title="Could not load vendors"
                    description={getErrorMessage(vendorsQuery.error)}
                    action={
                      <Button variant="secondary" onClick={() => void vendorsQuery.refetch()}>
                        Try again
                      </Button>
                    }
                  />
                ) : vendors.length === 0 ? (
                  <EmptyState
                    icon={<Wrench className="h-6 w-6" />}
                    title="No vendors yet"
                    description="Add society-empanelled vendors (plumber, electrician, AC tech) to assign complaints with SLAs."
                    action={
                      <Button onClick={openCreate}>
                        <Plus className="h-4 w-4" /> Add vendor
                      </Button>
                    }
                  />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead className="text-right">Rate / hr</TableHead>
                        <TableHead className="text-right">Rating</TableHead>
                        <TableHead className="text-right">Jobs</TableHead>
                        <TableHead className="text-right">Open</TableHead>
                        <TableHead className="text-right">Total paid</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vendors.map((v) => {
                        const openCount = totalsAssignmentsByVendor.get(v.id) ?? 0;
                        return (
                          <TableRow key={v.id}>
                            <TableCell>
                              <div className="flex items-center gap-vt-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
                                  <Wrench className="h-4 w-4" strokeWidth={1.75} />
                                </span>
                                <div className="min-w-0">
                                  <div className="font-medium text-vt-text">{v.name}</div>
                                  <div className="text-vt-12 text-vt-text-muted">
                                    {v.contact_name ?? '—'}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="muted">{CATEGORY_LABEL[v.category]}</Badge>
                            </TableCell>
                            <TableCell className="text-vt-text-muted">
                              <span className="inline-flex items-center gap-1 text-vt-13">
                                <Phone className="h-3.5 w-3.5" />
                                {v.phone}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                              {v.hourly_rate_inr ? formatInr(v.hourly_rate_inr) : '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="inline-flex items-center gap-1 text-vt-13 text-vt-text">
                                <Star className="h-3.5 w-3.5 text-vt-warn" />
                                {ratingLabel(v.rating_avg)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-vt-text-muted">{v.jobs_completed}</TableCell>
                            <TableCell className="text-right text-vt-text-muted">{openCount}</TableCell>
                            <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                              {formatInr(v.total_paid_inr)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={STATUS_VARIANT[v.status]}>{STATUS_LABEL[v.status]}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="relative inline-block">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title="More actions"
                                  onClick={() => toggleMenu(v.id)}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                                {openMenuId === v.id ? (
                                  <div
                                    className="absolute right-0 z-10 mt-1 w-44 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface shadow-vt-2"
                                    onMouseLeave={() => setOpenMenuId(null)}
                                  >
                                    <button
                                      type="button"
                                      className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        openEdit(v);
                                      }}
                                    >
                                      <Edit3 className="h-4 w-4" /> Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        openInvoiceDialog(v);
                                      }}
                                    >
                                      <Receipt className="h-4 w-4" /> Add invoice
                                    </button>
                                    {v.status === 'active' ? (
                                      <button
                                        type="button"
                                        className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-alert hover:bg-vt-surface-2"
                                        onClick={() => {
                                          setOpenMenuId(null);
                                          setDeactivateTarget(v);
                                        }}
                                      >
                                        <Power className="h-4 w-4" /> Deactivate
                                      </button>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
                <CursorPaginator
                  hasMore={Boolean(vendorPaginator.nextCursor)}
                  loading={vendorsQuery.isFetching}
                  itemsShown={vendors.length}
                  pageSize={50}
                  onLoadMore={() => {
                    if (vendorPaginator.nextCursor) setVendorCursor(vendorPaginator.nextCursor);
                  }}
                />
              </CardContent>
            </Card>
          </>
        ) : null}

        {tab === 'assignments' ? (
          <Card>
            <CardContent className="p-vt-4">
              {assignmentsQuery.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : assignmentsQuery.isError ? (
                <EmptyState
                  icon={<Clock3 className="h-6 w-6" />}
                  title="Could not load assignments"
                  description={getErrorMessage(assignmentsQuery.error)}
                  action={
                    <Button variant="secondary" onClick={() => void assignmentsQuery.refetch()}>
                      Try again
                    </Button>
                  }
                />
              ) : assignments.length === 0 ? (
                <EmptyState
                  icon={<Clock3 className="h-6 w-6" />}
                  title="No assignments yet"
                  description="Assign a complaint to a vendor from the Helpdesk to create one."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Complaint</TableHead>
                      <TableHead>SLA priority</TableHead>
                      <TableHead>Assigned</TableHead>
                      <TableHead>Due / status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map((a) => {
                      const cd = slaCountdownLabel(a.sla_due_at, a.resolved_at);
                      return (
                        <TableRow key={a.id}>
                          <TableCell>
                            <div className="font-medium text-vt-text">
                              {a.vendor?.name ?? '—'}
                            </div>
                            <div className="text-vt-12 text-vt-text-muted">
                              {a.vendor ? CATEGORY_LABEL[a.vendor.category] : ''}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="max-w-md">
                              <div className="truncate font-medium text-vt-text">
                                {a.complaint?.title ?? '—'}
                              </div>
                              <div className="line-clamp-1 text-vt-12 text-vt-text-muted">
                                {a.complaint?.body ?? ''}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={SLA_VARIANT[a.sla_priority]}>{SLA_LABEL[a.sla_priority]}</Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-vt-text-muted text-vt-12">
                            {formatDateTime(a.assigned_at)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={cd.variant}>{cd.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {a.resolved_at === null ? (
                              <Button size="sm" variant="secondary" onClick={() => openResolveDialog(a)}>
                                <CheckCircle2 className="h-4 w-4" /> Resolve
                              </Button>
                            ) : (
                              <span className="text-vt-12 text-vt-text-muted">
                                Resolved {formatDate(a.resolved_at)}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}

        {tab === 'invoices' ? (
          <Card>
            <CardContent className="p-vt-4">
              {invoicesQuery.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : invoicesQuery.isError ? (
                <EmptyState
                  icon={<Receipt className="h-6 w-6" />}
                  title="Could not load invoices"
                  description={getErrorMessage(invoicesQuery.error)}
                  action={
                    <Button variant="secondary" onClick={() => void invoicesQuery.refetch()}>
                      Try again
                    </Button>
                  }
                />
              ) : invoices.length === 0 ? (
                <EmptyState
                  icon={<Receipt className="h-6 w-6" />}
                  title="No invoices yet"
                  description="Create an invoice for a vendor from the Vendors tab kebab menu."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Paid at</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((i) => (
                      <TableRow key={i.id}>
                        <TableCell className="font-mono text-vt-13 text-vt-text">{i.invoice_number}</TableCell>
                        <TableCell>
                          <div className="font-medium text-vt-text">{i.vendor?.name ?? '—'}</div>
                          <div className="text-vt-12 text-vt-text-muted">
                            {i.vendor ? CATEGORY_LABEL[i.vendor.category] : ''}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                          {formatInr(i.amount_inr)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-vt-text-muted text-vt-12">
                          {formatDate(i.invoice_date)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={INVOICE_STATUS_VARIANT[i.status]}>
                            {INVOICE_STATUS_LABEL[i.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-vt-text-muted text-vt-12">
                          {i.paid_at ? formatDateTime(i.paid_at) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {i.status === 'unpaid' ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => markInvoicePaid.mutate(i.id)}
                              disabled={markInvoicePaid.isPending}
                            >
                              <CheckCircle2 className="h-4 w-4" /> Mark paid
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : null}
      </div>

      {vendorFormDialog}
      {invoiceFormDialog}
      {resolveDialog}

      <ConfirmDialog
        open={Boolean(deactivateTarget)}
        onOpenChange={(v) => (v ? null : setDeactivateTarget(null))}
        title="Deactivate vendor"
        description={
          deactivateTarget ? (
            <>
              Deactivate <strong>{deactivateTarget.name}</strong>? They no longer appear in the
              active roster and cannot receive new assignments. Existing assignments and invoices
              are retained.
            </>
          ) : null
        }
        confirmLabel="Deactivate"
        variant="danger"
        loading={deactivateVendor.isPending}
        onConfirm={() => {
          if (!deactivateTarget) return;
          deactivateVendor.mutate(deactivateTarget.id, {
            onSuccess: () => setDeactivateTarget(null)
          });
        }}
      />
    </>
  );
}
